import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { Group, List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import PostpartumTimeCell from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/PostpartumTable/PostpartumTimeCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getPostpartumHemorrhageRowValues } from "@/encounters/components/EncounterLabor/utils/postpartum";
import { usDateTimeFormat } from "@/utils/datetime";

interface PostpartumHemorrhageRowProps {
  time: string;
  observation: Observation;
  showNumbersOnly?: boolean;
  flowsheetId?: string;
  showPreviousVersions?: boolean;
  onRefresh?: () => Promise<void>;
  isViewOnly: boolean;
}

export default function PostpartumHemorrhageRow({
  time,
  observation,
  showNumbersOnly,
  flowsheetId,
  showPreviousVersions = true,
  onRefresh,
  isViewOnly,
}: PostpartumHemorrhageRowProps) {
  const {
    duration,
    maneuvers,
    medications,
    therapies,
    ivAccess,
    fluidVolume,
    hasAnswers,
    hasNotes,
    notes,
  } = useMemo(() => getPostpartumHemorrhageRowValues(observation), [observation]);

  if (!observation || (!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  const previousVersions = hasPreviousVersions(observation);

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <PostpartumTimeCell
          time={time}
          rowSpan={1}
          onRefresh={onRefresh}
          isViewOnly={isViewOnly}
          flowsheetId={flowsheetId}
          observation={observation}
        />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Postpartum Hemorrhage:</TextWrapper>
            <List size="sm" withPadding>
              {duration && (
                <List.Item>
                  <Group gap="xs">
                    <TextWrapper size="sm" variant="bold">
                      Duration:
                    </TextWrapper>
                    <TextWrapper size="sm">{duration}</TextWrapper>
                  </Group>
                </List.Item>
              )}
              {medications && medications.length > 0 && (
                <List.Item>
                  <TextWrapper size="sm" variant="bold">
                    Medication:
                  </TextWrapper>
                  <List withPadding type="ordered">
                    {medications?.map((med, index) => (
                      <List.Item key={`med-${index}`}>
                        <TextWrapper size="sm" variant="bold">
                          {med.medication
                            ? med.medication.charAt(0).toUpperCase() + med.medication.slice(1)
                            : "Medication not specified"}
                          :
                        </TextWrapper>
                        <List withPadding>
                          {med.form && (
                            <List.Item>
                              <Group gap="xs">
                                <TextWrapper size="sm" variant="bold">
                                  Form -
                                </TextWrapper>
                                <TextWrapper size="sm">{med.form}</TextWrapper>
                              </Group>
                            </List.Item>
                          )}
                          {med.dosage && (
                            <List.Item>
                              <Group gap="xs">
                                <TextWrapper size="sm" variant="bold">
                                  Dosage -
                                </TextWrapper>
                                <TextWrapper size="sm">{med.dosage}</TextWrapper>
                              </Group>
                            </List.Item>
                          )}
                          {med.frequency && (
                            <List.Item>
                              <Group gap="xs">
                                <TextWrapper size="sm" variant="bold">
                                  Frequency -
                                </TextWrapper>
                                <TextWrapper size="sm">{med.frequency}</TextWrapper>
                              </Group>
                            </List.Item>
                          )}
                          {med.duration && (
                            <List.Item>
                              <Group gap="xs">
                                <TextWrapper size="sm" variant="bold">
                                  Duration -
                                </TextWrapper>
                                <TextWrapper size="sm">
                                  {med.duration === "X days" && med.xDays
                                    ? `${med.xDays} Days`
                                    : med.duration}
                                </TextWrapper>
                              </Group>
                            </List.Item>
                          )}
                          {med.prn && (
                            <List.Item>
                              <Group gap="xs">
                                <TextWrapper size="sm" variant="bold">
                                  PRN -
                                </TextWrapper>
                                <TextWrapper size="sm">{med.prn}</TextWrapper>
                              </Group>
                            </List.Item>
                          )}
                          {med.specialInstructions && (
                            <List.Item>
                              <Group gap="xs">
                                <TextWrapper size="sm" variant="bold">
                                  Special Instructions -
                                </TextWrapper>
                                <TextWrapper size="sm">{med.specialInstructions}</TextWrapper>
                              </Group>
                            </List.Item>
                          )}
                        </List>
                      </List.Item>
                    ))}
                  </List>
                </List.Item>
              )}
              {therapies && therapies.length > 0 && (
                <List.Item>
                  <TextWrapper size="sm" variant="bold">
                    Therapy:
                  </TextWrapper>
                  <List withPadding type="ordered">
                    {therapies?.map((therapy, index) => (
                      <List.Item key={`therapy-${index}`}>
                        <TextWrapper size="sm" variant="bold">
                          {therapy.therapy
                            ? therapy.therapy.charAt(0).toUpperCase() + therapy.therapy.slice(1)
                            : "Therapy not specified"}
                          :
                        </TextWrapper>
                        {therapy.reason && (
                          <List withPadding>
                            <List.Item>
                              <Group gap="xs">
                                <TextWrapper size="sm" variant="bold">
                                  Reason -
                                </TextWrapper>
                                <TextWrapper size="sm">{therapy.reason}</TextWrapper>
                              </Group>
                            </List.Item>
                          </List>
                        )}
                      </List.Item>
                    ))}
                  </List>
                </List.Item>
              )}
              {maneuvers && maneuvers.length > 0 && (
                <List.Item>
                  <TextWrapper size="sm" variant="bold">
                    Maneuvers:
                  </TextWrapper>
                  <List size="sm" withPadding listStyleType="decimal">
                    {maneuvers?.map(
                      (maneuver, index) =>
                        maneuver.maneuver && (
                          <List.Item key={`maneuver-${index}`}>
                            <Group gap="xs">
                              <TextWrapper size="sm" variant="bold">
                                Type -
                              </TextWrapper>
                              <TextWrapper size="sm">{maneuver.maneuver}</TextWrapper>
                            </Group>
                          </List.Item>
                        ),
                    )}
                  </List>
                </List.Item>
              )}
              {ivAccess && (
                <List.Item>
                  <Group gap="xs">
                    <TextWrapper size="sm" variant="bold">
                      IV Access -
                    </TextWrapper>
                    <TextWrapper>{usDateTimeFormat(ivAccess)}</TextWrapper>
                  </Group>
                </List.Item>
              )}
              {fluidVolume && (
                <List.Item>
                  <Group gap="xs">
                    <TextWrapper size="sm" variant="bold">
                      Fluid Volume -
                    </TextWrapper>
                    <TextWrapper>{fluidVolume} mL</TextWrapper>
                  </Group>
                </List.Item>
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Postpartum Hemorrhage Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={PostpartumHemorrhageRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
    </Table.Tr>
  );
}
